/**
 * Functionality needed in views that allow selecting Decoders.
 *
 * Example would be a page that allows selecting Decoders to which a
 * specific tv-clip or ticker message is added to.
 *
 * Important: Each page that includes this mixin must provide its own
 *   this.decoders, which this mixin depends on! This is done to allow
 *   to filter down the list for each page with differing logic.
 */

import filter from 'lodash.filter';
import groupBy from 'lodash.groupby';
import orderBy from 'lodash.orderby';

class StoreDecodersWrapper {
  constructor(store, decoders) {
    this.store = store;
    this.decoders = decoders;
  }
}

var addDecoderTo = {
  data() {
    return {
      loading: false,
      showConfirmationDialog: false,
      showOfflineDecoderSelectedDialog: false,
      adding: false,
      searchText: '',
    };
  },
  computed: {
    // A map to keep track which Decoders are currently selected.
    // Decoders are only added when their checkbox is clicked, so
    // non-contained Decoders are considered 'not selected', as are
    // those that are contained but false.
    decoderIdentToSelected() {
      return this.$store.state.common.decoderIdentToSelected;
    },
    atLeastOneDecoderSelected() {
      return this.getSelectedDecoders().length > 0;
    },
    allSelected: {
      get() {
        if (!this.decoderIdentToSelected) {
          return false;
        }
        const selections = Object.values(this.decoderIdentToSelected);
        return selections.length > 0 && selections.every((val) => !!val);
      },
      set(selected) {
        if (!this.$refs.selectableStoreDecoders) {
          return;
        }
        const wrapperDecoderIdents = [];
        for (let wrapper of this.filteredStoreDecoderWrappers) {
          for (let decoder of wrapper.decoders) {
            wrapperDecoderIdents.push(decoder.ident);
          }
        }
        const opts = {
          selected: selected,
          decoderIdents: wrapperDecoderIdents,
        };
        this.$store.commit('SET_DECODERS_SELECTED_BY_IDENT', opts);
      },
    },
    someSelected() {
      return (
        Object.values(this.decoderIdentToSelected).some((val) => !!val) &&
        !this.allSelected
      );
    },
    dialogTitle() {
      const numDecoders = this.getSelectedDecoders().length;
      if (numDecoders === 1) {
        return this.$t('batchAddDecodersTitleSingular');
      }
      let template = this.$t('batchAddDecodersTitle');
      let text = template.replace('{{ numDecoders }}', numDecoders);
      return text;
    },
    /**
     * Return a list of helper objects wrapping a Store and its Decoders.
     *
     * This list is already filtered by the search text.
     */
    filteredStoreDecoderWrappers() {
      let vm = this;
      let wrappers = [];

      let searchText = vm.searchText ? vm.searchText.toLowerCase() : '';
      let decodersGroupedByStore = groupBy(this.decoders, 'store.id');

      let storeIds = Object.keys(decodersGroupedByStore);
      storeIds = orderBy(storeIds, (storeId) => {
        let decoders = decodersGroupedByStore[storeId];
        let firstDecoder = decoders[0];
        return firstDecoder.store.city;
      });
      storeIds.forEach((storeId) => {
        let decoders = decodersGroupedByStore[storeId];
        let firstDecoder = decoders[0];

        let store = firstDecoder.store;
        let storeDecoders = this.decoders.filter(
          (decoder) => decoder.store.id === store.id
        );

        let filteredStoreDecoders = filter(storeDecoders, (decoder) => {
          let label =
            decoder.description +
            ' ' +
            decoder.store.city +
            ' ' +
            decoder.store.name +
            ' ' +
            decoder.store.address +
            ' ' +
            decoder.store.location;
          if (decoder.ctype === 'radio' && decoder.channel) {
            label += ' ' + decoder.channel.name;
          }
          label = label.toLowerCase();
          let matches = label.indexOf(searchText) != -1;
          return matches;
        });

        if (!filteredStoreDecoders.length) {
          return;
        }
        let wrapper = new StoreDecodersWrapper(store, filteredStoreDecoders);
        wrappers.push(wrapper);
      });
      return wrappers;
    },
    searchLabel() {
      let overallCount = 0;
      const counts = this.filteredStoreDecoderWrappers.map(
        (wrapper) => wrapper.decoders.length
      );
      if (counts.length) {
        overallCount = counts.reduce((a, b) => a + b);
      }

      if (overallCount > 0) {
        return `${this.$t('search')} (${overallCount}) ...`;
      }
      return this.$t('search') + ' ...';
    },
  },
  watch: {
    /**
     * Populate decoder selection lookup initially.
     */
    decoders: function (decoders) {
      const selections = Object.values(this.decoderIdentToSelected);

      if (decoders.length !== selections.length) {
        // We don't want to overwrite any manual user changes later on.
        if (this.someSelected || this.allSelected) {
          return;
        }

        // Initialize as all being deselected.
        const opts = {
          decoderIdents: decoders.map((decoder) => decoder.ident),
          selected: false,
        };
        this.$store.commit('SET_DECODERS_SELECTED_BY_IDENT', opts);
      }
    },
    /**
     * If a Decoder has been selected, make the User confirm any route changes.
     */
    decoderIdentToSelected: {
      deep: true,
      handler: function () {
        if (this.atLeastOneDecoderSelected) {
          this.$store.commit(
            'SET_PRE_ROUTE_QUESTION',
            this.$t('selectedDecodersRouteConfirm')
          );
        } else {
          this.$store.commit('UNSET_PRE_ROUTE_QUESTION');
        }
      },
    },
  },
  methods: {
    getSelectedDecoders() {
      let selected = [];
      for (let decoderIdent in this.decoderIdentToSelected) {
        if (!this.decoderIdentToSelected.hasOwnProperty(decoderIdent)) {
          continue;
        }
        const isSelected = this.decoderIdentToSelected[decoderIdent];
        if (isSelected) {
          for (var i = 0; i < this.decoders.length; i++) {
            let decoder = this.decoders[i];
            if (decoder.ident == decoderIdent) {
              selected.push(decoder);
              break;
            }
          }
        }
      }
      return selected;
    },
    getSelectedOfflineDecoders() {
      return this.getSelectedDecoders().filter((d) => !d.is_online);
    },
    focusSearch() {
      this.$refs.searchbar.focus();
    },
    clearSearch() {
      this.searchText = '';
    },
    resetDecoderSelection() {
      this.$store.commit('DESELECT_ALL_DECODERS');
    },
    reset() {
      this.resetDecoderSelection();
      this.$refs.confirmationDialog.reset();
    },
    handleDialogAbortion() {
      this.showConfirmationDialog = false;
      this.$refs.confirmationDialog.reset();
    },
  },
  /**
   * Always start with a blank selection.
   *
   * If we don't do this the state.decoderIdentToSelected is remembered
   * from the last (possibly different) component that changed it.
   */
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.resetDecoderSelection();
    });
  },
};

export default addDecoderTo;



// WEBPACK FOOTER //
// ./src/mixins/addDecoderTo.js